import TableAction from "components/TableAction";
import PropTypes from "prop-types";
// import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PreviewIcon from "@mui/icons-material/Preview";
import GenericMoreButton from "components/GenericMoreButton";
import { useAppContextController } from "context/AppContext";
import { useCallback, useMemo } from "react";
import { isEmpty, isEqual } from "lodash";
import fetchCustomers from "api/companies/fetchCustomers";
import { useQuery } from "react-query";
import { AccountBalance } from "@mui/icons-material";

const PayrollTableActions = ({
  eventUrl,
  setNavigateUrl,
  setNavigateUrlToScreen,
  row,
  setActionSelected,
  setFilters,
  setOpen,
  setEventPreview,
  setJobPreview,
  getAndSetFullEvent,
  getAndSetFullJob,
  openTimeModal,
}) => {
  const { userType, company, currentLoggedUser } = useAppContextController();

  const isProduction = process.env.REACT_APP_ENV?.trim() === "production";
  const shouldShowRestrictedEditors =
    company?.payrollAuthorizedEmails?.includes(currentLoggedUser?.emailAddress) || !isProduction;

  const isCompany = company?.companyType === "Company";
  const onClickHandler = useCallback(
    (action, e) => {
      e.stopPropagation();

      if (action === "jobdetails" && !isCompany) {
        setNavigateUrlToScreen(`/jobs/${row?.jobSlug}/action/jobinfo`);
        return;
      }
      if (action === "jobdetails" && isCompany) {
        setNavigateUrlToScreen(`/companyjobs/${row?.jobSlug}/action/jobinfo`);
        return;
      }
      if (action === "eventdetails") {
        setNavigateUrlToScreen(`/events/${row?.eventUrl}/action/eventsinfo`);
        return;
      }

      const nodes = new URL(window.location).pathname.split("/");
      let baseRoute = `/payroll/event/${row?.eventUrl}`;
      if (row?.jobSlug) baseRoute = `/payroll/job/${row?.jobSlug}`;

      // we need to get rid of timeFrame in the filters, because user has selected
      //    and event which could be in the past
      if (row?.eventUrl)
        setFilters((prev) => {
          const newFilters = { ...prev };
          // if (newFilters.timeFrame) delete newFilters.timeFrame;
          return { ...newFilters, eventUrl: row.eventUrl };
        });
      // setCurrentEvent(row);
      if (row?.jobSlug) getAndSetFullJob(row._id);
      else if (row?.eventUrl) getAndSetFullEvent(row._id);
      setNavigateUrl(`${baseRoute}/action/${action}`);
    },
    [
      getAndSetFullEvent,
      getAndSetFullJob,
      isCompany,
      row._id,
      row.eventUrl,
      row?.jobSlug,
      setFilters,
      setNavigateUrl,
      setNavigateUrlToScreen,
    ]
  );

  const { data: customers, isLoading } = useQuery(
    ["customerdropdown"],
    () =>
      fetchCustomers({
        filters: { status: "Active" },
        fetchAll: true,
        order: "asc",
        orderBy: "name",
      }),
    {
      enabled: isCompany,
    }
  );
  const openPreviewModal = useCallback(() => {
    if (isCompany) {
      if (!isEmpty(row.companyName)) {
        const customerInfo = customers?.data.find(
          (customer) =>
            isEqual(customer.name, row.companyName) || row.companySlug.includes(customer.slug)
        );

        setJobPreview({
          ...customerInfo,
          ...row,
        });
      }
    } else if (row?.jobSlug) setJobPreview(row);
    else setEventPreview(row);
    setOpen((prev) => !prev);
  }, [customers?.data, isCompany, row, setEventPreview, setJobPreview, setOpen]);

  const filteredActions = useMemo(
    () => [
      {
        color: "info",
        icon: "info",
        clickAction: (e) => onClickHandler(row?.jobSlug ? "jobinfo" : "eventsinfo", e),
        tooltip: row?.jobSlug ? "Job Info" : "Event Info",
      },
      {
        color: row?.jobSlug ? "warning" : "success",
        icon: row?.jobSlug ? "work" : "event-seat",
        clickAction: (e) => onClickHandler(row?.jobSlug ? "jobdetails" : "eventdetails", e),
        tooltip: row?.jobSlug ? "Go to Job" : "Go to Event",
      },
      {
        color: "error",
        icon: "schedule",
        clickAction: (e) => onClickHandler("timecard", e),
        tooltip: "Timecard Editor",
      },
      ...(shouldShowRestrictedEditors
        ? [
            {
              color: "success",
              icon: "request_quote",
              clickAction: (e) => onClickHandler("payroll", e),
              tooltip: "Payroll Editor",
            },
            {
              color: "primary",
              icon: <AccountBalance color="violet" />,
              clickAction: (e) => onClickHandler("invoice", e),
              tooltip: "Invoice Editor",
            },
          ]
        : []),
      {
        name: "eventPreview",
        color: "info",
        icon: <PreviewIcon />,
        clickAction: (e) => {
          e.stopPropagation();
          openPreviewModal();
        },
        tooltip: row?.jobSlug ? "Job Preview" : "Event Preview",
      },
    ],
    [onClickHandler, openPreviewModal, row?.jobSlug, shouldShowRestrictedEditors]
  );

  if (filteredActions.length <= 2) {
    return (
      <span>
        {filteredActions.map((prop) => (
          <TableAction {...prop} key={prop.tooltip} />
        ))}
      </span>
    );
  }

  return (
    <span>
      {filteredActions.slice(0, 1).map((prop) => (
        <TableAction {...prop} key={prop.tooltip} />
      ))}
      <GenericMoreButton buttons={filteredActions.slice(1)} />
    </span>
  );
};

export default PayrollTableActions;

PayrollTableActions.defaultProps = {
  eventUrl: "",
};

// Typechecking props for the TableAction
PayrollTableActions.propTypes = {
  eventUrl: PropTypes.string,
  // children: PropTypes.node.isRequired,
};
